import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
  updateDoc,
  onSnapshot,
  addDoc,
  deleteDoc,
  increment,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';
import { getFriends, getFollowing } from './socialService';

// ============================================================================
// TYPES
// ============================================================================

export interface Comment {
  id?: string; // For identifying comments for replies
  odD: string;
  username: string;
  avatarUrl?: string;
  text: string;
  createdAt: Timestamp | Date;
  replyTo?: {
    commentId: string;
    username: string;
  };
  replies?: Comment[]; // Nested replies
}

export interface Story {
  id: string;
  odD: string;
  username: string;
  avatarUrl?: string;
  imageUrl: string;
  caption?: string;
  
  potAmount: number;
  currency: 'pkcash' | 'playcoins';
  handType?: string;
  tableName?: string;
  
  createdAt: Timestamp;
  expiresAt: Timestamp;
  
  views: number;
  viewedBy: string[];
  likes: number;
  likedBy: string[];
  comments?: Comment[];
  
  visibility: 'public' | 'friends' | 'private';
}

export interface StoryGroup {
  odD: string;
  username: string;
  avatarUrl?: string;
  stories: Story[];
  hasUnwatched: boolean;
  latestStoryAt: Timestamp;
}

export interface FeedPost extends Story {
  commentCount?: number;
}

// ============================================================================
// WIN THRESHOLDS
// ============================================================================

export const WIN_THRESHOLDS = {
  pkcash: {
    minimum: 20,
    bigWin: 100,
    monster: 500,
  },
  playcoins: {
    minimum: 2000,
    bigWin: 10000,
    monster: 50000,
  },
};

export const isShareableWin = (
  amount: number, 
  currency: 'pkcash' | 'playcoins',
  bigBlind?: number
): boolean => {
  const thresholds = WIN_THRESHOLDS[currency];
  if (amount >= thresholds.minimum) return true;
  if (bigBlind && amount >= bigBlind * 10) return true;
  return false;
};

export const getWinTier = (
  amount: number, 
  currency: 'pkcash' | 'playcoins'
): 'normal' | 'big' | 'monster' => {
  const thresholds = WIN_THRESHOLDS[currency];
  if (amount >= thresholds.monster) return 'monster';
  if (amount >= thresholds.bigWin) return 'big';
  return 'normal';
};

// ============================================================================
// STORY CRUD OPERATIONS
// ============================================================================

export const createStory = async (
  userId: string,
  username: string,
  avatarUrl: string | undefined,
  imageBlob: Blob,
  potAmount: number,
  currency: 'pkcash' | 'playcoins',
  handType?: string,
  tableName?: string,
  caption?: string,
  visibility: 'public' | 'friends' | 'private' = 'friends'
): Promise<string> => {
  try {
    const timestamp = Date.now();
    const imagePath = `stories/${userId}/${timestamp}.jpg`;
    const imageRef = ref(storage, imagePath);
    
    await uploadBytes(imageRef, imageBlob, { contentType: 'image/jpeg' });
    const imageUrl = await getDownloadURL(imageRef);
    
    const now = new Date();
    const expiresAt = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    
    const storyRef = await addDoc(collection(db, 'stories'), {
      odD: userId,
      username,
      avatarUrl: avatarUrl || null,
      imageUrl,
      caption: caption || null,
      potAmount,
      currency,
      handType: handType || null,
      tableName: tableName || null,
      createdAt: serverTimestamp(),
      expiresAt: Timestamp.fromDate(expiresAt),
      views: 0,
      viewedBy: [],
      likes: 0,
      likedBy: [],
      comments: [],
      visibility,
    });
    
    // Send notifications to @mentioned users
    if (caption) {
      await sendMentionNotifications(storyRef.id, userId, username, caption, potAmount, currency);
    }
    
    return storyRef.id;
  } catch (error) {
    console.error('Error creating story:', error);
    throw error;
  }
};

export const getFriendStories = async (userId: string): Promise<StoryGroup[]> => {
  try {
    const friends = await getFriends(userId);
    const following = await getFollowing(userId);
    
    const userIds = new Set<string>([userId]);
    friends.forEach(f => userIds.add(f.userId));
    following.forEach(f => userIds.add(f.userId));
    
    if (userIds.size === 0) return [];
    
    const now = Timestamp.now();
    const userIdArray = Array.from(userIds);
    
    const batches: Story[][] = [];
    for (let i = 0; i < userIdArray.length; i += 30) {
      const batch = userIdArray.slice(i, i + 30);
      const q = query(
        collection(db, 'stories'),
        where('odD', 'in', batch),
        where('expiresAt', '>', now),
        orderBy('expiresAt', 'asc')
      );
      
      const snapshot = await getDocs(q);
      const stories = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Story[];
      
      batches.push(stories);
    }
    
    const allStories = batches.flat();
    const groupedByUser = new Map<string, Story[]>();
    
    allStories.forEach(story => {
      if (story.visibility === 'private' && story.odD !== userId) return;
      if (story.visibility === 'friends') {
        const isFriend = friends.some(f => f.userId === story.odD);
        if (!isFriend && story.odD !== userId) return;
      }
      
      const existing = groupedByUser.get(story.odD) || [];
      existing.push(story);
      groupedByUser.set(story.odD, existing);
    });
    
    const storyGroups: StoryGroup[] = [];
    groupedByUser.forEach((stories, odD) => {
      if (stories.length === 0) return;
      
      const firstStory = stories[0];
      const hasUnwatched = stories.some(s => !s.viewedBy?.includes(userId));
      const latestStoryAt = stories.reduce((latest, s) => 
        s.createdAt.toMillis() > latest.toMillis() ? s.createdAt : latest,
        stories[0].createdAt
      );
      
      storyGroups.push({
        odD,
        username: firstStory.username,
        avatarUrl: firstStory.avatarUrl,
        stories: stories.sort((a, b) => a.createdAt.toMillis() - b.createdAt.toMillis()),
        hasUnwatched,
        latestStoryAt,
      });
    });
    
    storyGroups.sort((a, b) => {
      if (a.odD === userId) return -1;
      if (b.odD === userId) return 1;
      if (a.hasUnwatched && !b.hasUnwatched) return -1;
      if (!a.hasUnwatched && b.hasUnwatched) return 1;
      return b.latestStoryAt.toMillis() - a.latestStoryAt.toMillis();
    });
    
    return storyGroups;
  } catch (error) {
    console.error('Error getting friend stories:', error);
    return [];
  }
};

export const subscribeToFriendStories = (
  userId: string,
  friendIds: string[],
  callback: (stories: StoryGroup[]) => void
): (() => void) => {
  const now = Timestamp.now();
  const allUserIds = [userId, ...friendIds].slice(0, 30);
  
  const q = query(
    collection(db, 'stories'),
    where('odD', 'in', allUserIds),
    where('expiresAt', '>', now),
    orderBy('expiresAt', 'desc'),
    orderBy('createdAt', 'desc')
  );
  
  return onSnapshot(q, async (snapshot) => {
    const stories = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Story[];
    
    const groupedByUser = new Map<string, Story[]>();
    stories.forEach(story => {
      const existing = groupedByUser.get(story.odD) || [];
      existing.push(story);
      groupedByUser.set(story.odD, existing);
    });
    
    const storyGroups: StoryGroup[] = [];
    groupedByUser.forEach((userStories, odD) => {
      if (userStories.length === 0) return;
      
      const firstStory = userStories[0];
      const hasUnwatched = userStories.some(s => !s.viewedBy?.includes(userId));
      const latestStoryAt = userStories.reduce((latest, s) => 
        s.createdAt.toMillis() > latest.toMillis() ? s.createdAt : latest,
        userStories[0].createdAt
      );
      
      storyGroups.push({
        odD,
        username: firstStory.username,
        avatarUrl: firstStory.avatarUrl,
        stories: userStories.sort((a, b) => a.createdAt.toMillis() - b.createdAt.toMillis()),
        hasUnwatched,
        latestStoryAt,
      });
    });
    
    storyGroups.sort((a, b) => {
      if (a.odD === userId) return -1;
      if (b.odD === userId) return 1;
      if (a.hasUnwatched && !b.hasUnwatched) return -1;
      if (!a.hasUnwatched && b.hasUnwatched) return 1;
      return b.latestStoryAt.toMillis() - a.latestStoryAt.toMillis();
    });
    
    callback(storyGroups);
  }, (error) => {
    console.error('Error subscribing to stories:', error);
  });
};

export const getFeedPosts = async (
  userId: string,
  limitCount: number = 20,
  beforeTimestamp?: Timestamp
): Promise<FeedPost[]> => {
  try {
    const friends = await getFriends(userId);
    const friendIds = new Set(friends.map(f => f.userId));
    
    const now = Timestamp.now();
    const q = query(
      collection(db, 'stories'),
      where('expiresAt', '>', now),
      orderBy('expiresAt', 'asc'),
      limit(100)
    );
    
    const snapshot = await getDocs(q);
    const posts = snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data(),
        commentCount: (doc.data().comments || []).length,
      } as FeedPost))
      .filter(post => {
        if (post.visibility === 'private' && post.odD !== userId) return false;
        if (post.visibility === 'friends' && post.odD !== userId && !friendIds.has(post.odD)) return false;
        return true;
      })
      .sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis())
      .slice(0, limitCount);
    
    return posts;
  } catch (error) {
    console.error('Error getting feed posts:', error);
    return [];
  }
};

// ============================================================================
// ENGAGEMENT FUNCTIONS
// ============================================================================

export const viewStory = async (storyId: string, userId: string): Promise<void> => {
  try {
    const storyRef = doc(db, 'stories', storyId);
    await updateDoc(storyRef, {
      views: increment(1),
      viewedBy: arrayUnion(userId),
    });
  } catch (error) {
    console.error('Error viewing story:', error);
  }
};

export const toggleLikeStory = async (storyId: string, userId: string): Promise<boolean> => {
  try {
    const storyRef = doc(db, 'stories', storyId);
    const storySnap = await getDoc(storyRef);
    
    if (!storySnap.exists()) throw new Error('Story not found');
    
    const story = storySnap.data() as Story;
    const isLiked = story.likedBy?.includes(userId) || false;
    
    if (isLiked) {
      await updateDoc(storyRef, {
        likes: increment(-1),
        likedBy: arrayRemove(userId),
      });
      return false;
    } else {
      await updateDoc(storyRef, {
        likes: increment(1),
        likedBy: arrayUnion(userId),
      });
      return true;
    }
  } catch (error) {
    console.error('Error toggling like:', error);
    throw error;
  }
};

export const addComment = async (
  storyId: string,
  userId: string,
  username: string,
  avatarUrl: string | undefined,
  text: string,
  replyTo?: { commentId: string; username: string }
): Promise<void> => {
  try {
    const storyRef = doc(db, 'stories', storyId);
    const newComment: Comment = {
      id: `${userId}_${Date.now()}`, // Unique ID for the comment
      odD: userId,
      username,
      avatarUrl: avatarUrl || undefined,
      text,
      createdAt: Timestamp.now(),
      ...(replyTo && { replyTo }), // Add replyTo if provided
    };
    
    await updateDoc(storyRef, {
      comments: arrayUnion(newComment),
    });
    
    // Send DMs to @mentioned users in the comment
    await sendCommentMentionNotifications(storyId, userId, username, text);
    
    // If this is a reply, notify the person being replied to
    if (replyTo && replyTo.username.toLowerCase() !== username.toLowerCase()) {
      await sendReplyNotification(storyId, userId, username, replyTo.username);
    }
    
  } catch (error) {
    console.error('Error adding comment:', error);
    throw error;
  }
};

export const deleteStory = async (storyId: string, userId: string): Promise<void> => {
  try {
    const storyRef = doc(db, 'stories', storyId);
    const storySnap = await getDoc(storyRef);
    
    if (!storySnap.exists()) throw new Error('Story not found');
    
    const story = storySnap.data() as Story;
    if (story.odD !== userId) throw new Error('Not authorized to delete this story');
    
    await deleteDoc(storyRef);
  } catch (error) {
    console.error('Error deleting story:', error);
    throw error;
  }
};

export const getMyStories = async (userId: string): Promise<Story[]> => {
  try {
    const now = Timestamp.now();
    const q = query(
      collection(db, 'stories'),
      where('odD', '==', userId),
      where('expiresAt', '>', now),
      orderBy('expiresAt', 'desc'),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Story[];
  } catch (error) {
    console.error('Error getting my stories:', error);
    return [];
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export const formatWinAmount = (amount: number, currency: 'pkcash' | 'playcoins'): string => {
  if (currency === 'pkcash') {
    return `$${amount.toFixed(2)}`;
  } else {
    return `${amount.toLocaleString()} PC`;
  }
};

export const getTimeAgo = (timestamp: Timestamp | Date | any): string => {
  if (!timestamp) return '';
  
  const date = timestamp.toDate ? timestamp.toDate() : 
               timestamp instanceof Date ? timestamp : new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return date.toLocaleDateString();
};

export const getExpiresIn = (expiresAt: Timestamp): string => {
  const now = new Date();
  const expires = expiresAt.toDate();
  const diffMs = expires.getTime() - now.getTime();
  const diffHours = Math.floor(diffMs / 3600000);
  const diffMins = Math.floor((diffMs % 3600000) / 60000);
  
  if (diffMs <= 0) return 'Expired';
  if (diffHours > 0) return `${diffHours}h ${diffMins}m`;
  return `${diffMins}m`;
};


// ============================================================================
// MENTION NOTIFICATIONS (via Direct Messages)
// ============================================================================

/**
 * Extract @mentions from text
 */
const extractMentions = (text: string): string[] => {
  const mentions = text.match(/@(\w+)/g);
  if (!mentions) return [];
  return [...new Set(mentions.map(m => m.slice(1).toLowerCase()))];
};

/**
 * Find user ID by username
 */
const findUserByUsername = async (username: string): Promise<string | null> => {
  try {
    const usersRef = collection(db, 'users');
    
    // Try usernameLower first
    let q = query(usersRef, where('usernameLower', '==', username.toLowerCase()), limit(1));
    let snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      return snapshot.docs[0].id;
    }
    
    // Try exact username match
    q = query(usersRef, where('username', '==', username), limit(1));
    snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      return snapshot.docs[0].id;
    }
    
    // Try case-insensitive username (if stored differently)
    q = query(usersRef, where('username', '==', username.toLowerCase()), limit(1));
    snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      return snapshot.docs[0].id;
    }
    
    console.log(`[Mention] User not found: ${username}`);
    return null;
  } catch (error) {
    console.error('Error finding user by username:', error);
    return null;
  }
};

/**
 * Get conversation ID (same logic as messageService)
 */
const getConversationId = (uid1: string, uid2: string): string => {
  return [uid1, uid2].sort().join('_');
};

/**
 * Send a DM to mentioned user about the story
 */
const sendMentionDM = async (
  fromUserId: string,
  fromUsername: string,
  mentionedUserId: string,
  storyId: string,
  potAmount: number,
  currency: 'pkcash' | 'playcoins'
): Promise<void> => {
  try {
    const conversationId = getConversationId(fromUserId, mentionedUserId);
    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationSnap = await getDoc(conversationRef);
    
    // Create conversation if it doesn't exist
    if (!conversationSnap.exists()) {
      await setDoc(conversationRef, {
        participants: [fromUserId, mentionedUserId].sort(),
        lastMessage: '',
        lastMessageAt: serverTimestamp(),
        lastMessageBy: '',
        createdAt: serverTimestamp(),
      });
    }
    
    // Send the mention message
    const messageText = ` I tagged you in my ${formatWinAmount(potAmount, currency)} win! Check it out in the Feed `;

    
    const messagesRef = collection(db, 'conversations', conversationId, 'messages');
    await addDoc(messagesRef, {
      senderId: fromUserId,
      text: messageText,
      createdAt: serverTimestamp(),
      read: false,
      type: 'mention', // Special type for styling
      storyId, // Link to the story
    });
    
    // Update conversation with last message
    await updateDoc(conversationRef, {
      lastMessage: messageText.substring(0, 100),
      lastMessageAt: serverTimestamp(),
      lastMessageBy: fromUserId,
    });
    
    console.log(`📨 Sent mention DM to ${mentionedUserId}`);
  } catch (error) {
    console.error('Error sending mention DM:', error);
  }
};

/**
 * Process caption and send DMs to all mentioned users
 */
const sendMentionNotifications = async (
  storyId: string,
  fromUserId: string,
  fromUsername: string,
  caption: string,
  potAmount: number,
  currency: 'pkcash' | 'playcoins'
): Promise<void> => {
  const mentionedUsernames = extractMentions(caption);
  
  for (const username of mentionedUsernames) {
    // Don't notify yourself
    if (username.toLowerCase() === fromUsername.toLowerCase()) continue;
    
    const mentionedUserId = await findUserByUsername(username);
    if (mentionedUserId && mentionedUserId !== fromUserId) {
      await sendMentionDM(fromUserId, fromUsername, mentionedUserId, storyId, potAmount, currency);
    }
  }
};

/**
 * Send DMs to users mentioned in a comment
 */
const sendCommentMentionNotifications = async (
  storyId: string,
  fromUserId: string,
  fromUsername: string,
  commentText: string
): Promise<void> => {
  const mentionedUsernames = extractMentions(commentText);
  console.log(`[Mention] Found ${mentionedUsernames.length} mentions in comment:`, mentionedUsernames);
  
  for (const username of mentionedUsernames) {
    // Don't notify yourself
    if (username.toLowerCase() === fromUsername.toLowerCase()) {
      console.log(`[Mention] Skipping self-mention: ${username}`);
      continue;
    }
    
    console.log(`[Mention] Looking up user: ${username}`);
    const mentionedUserId = await findUserByUsername(username);
    console.log(`[Mention] Found userId: ${mentionedUserId}`);
    
    if (mentionedUserId && mentionedUserId !== fromUserId) {
      console.log(`[Mention] Sending DM to ${mentionedUserId}`);
      await sendCommentMentionDM(fromUserId, fromUsername, mentionedUserId, storyId);
    }
  }
};

/**
 * Send a DM for comment @mention
 */
const sendCommentMentionDM = async (
  fromUserId: string,
  fromUsername: string,
  mentionedUserId: string,
  storyId: string
): Promise<void> => {
  try {
    const conversationId = getConversationId(fromUserId, mentionedUserId);
    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationSnap = await getDoc(conversationRef);
    
    // Create conversation if it doesn't exist
    if (!conversationSnap.exists()) {
      await setDoc(conversationRef, {
        participants: [fromUserId, mentionedUserId].sort(),
        lastMessage: '',
        lastMessageAt: serverTimestamp(),
        lastMessageBy: '',
        createdAt: serverTimestamp(),
      });
    }
    
    // Send the mention message
    const messageText = ` ${fromUsername} mentioned you in a comment! Check it out in the Feed`;

    
    const messagesRef = collection(db, 'conversations', conversationId, 'messages');
    await addDoc(messagesRef, {
      senderId: fromUserId,
      text: messageText,
      createdAt: serverTimestamp(),
      read: false,
      type: 'comment_mention',
      storyId,
    });
    
    // Update conversation with last message
    await updateDoc(conversationRef, {
      lastMessage: messageText.substring(0, 100),
      lastMessageAt: serverTimestamp(),
      lastMessageBy: fromUserId,
    });
    
    console.log(`📨 Sent comment mention DM to ${mentionedUserId}`);
  } catch (error) {
    console.error('Error sending comment mention DM:', error);
  }
};

/**
 * Send a DM when someone replies to a comment
 */
const sendReplyNotification = async (
  storyId: string,
  fromUserId: string,
  fromUsername: string,
  replyToUsername: string
): Promise<void> => {
  try {
    // Find the user being replied to
    const repliedToUserId = await findUserByUsername(replyToUsername);
    if (!repliedToUserId || repliedToUserId === fromUserId) return;

    const conversationId = getConversationId(fromUserId, repliedToUserId);
    const conversationRef = doc(db, 'conversations', conversationId);
    const conversationSnap = await getDoc(conversationRef);
    
    // Create conversation if it doesn't exist
    if (!conversationSnap.exists()) {
      await setDoc(conversationRef, {
        participants: [fromUserId, repliedToUserId].sort(),
        lastMessage: '',
        lastMessageAt: serverTimestamp(),
        lastMessageBy: '',
        createdAt: serverTimestamp(),
      });
    }
    
    const messageText = ` ${fromUsername} replied to your comment! Check it out in the Feed`;

    
    const messagesRef = collection(db, 'conversations', conversationId, 'messages');
    await addDoc(messagesRef, {
      senderId: fromUserId,
      text: messageText,
      createdAt: serverTimestamp(),
      read: false,
      type: 'comment_reply',
      storyId,
    });
    
    await updateDoc(conversationRef, {
      lastMessage: messageText.substring(0, 100),
      lastMessageAt: serverTimestamp(),
      lastMessageBy: fromUserId,
    });
    
    console.log(`💬 Sent reply notification DM to ${repliedToUserId}`);
  } catch (error) {
    console.error('Error sending reply notification:', error);
  }
};